import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit7Notes: UnitNotes = {
  unitId: "unit-7",
  unitName: "Unit 7: Oscillations",
  examWeight: "5–8%",
  focus: "Simple Harmonic Motion (SHM), periodicity, and energy conservation in oscillating systems.",
  sections: [
    {
      id: "topic-7-1",
      title: "Topic 7.1: Defining Simple Harmonic Motion (SHM)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔂 The Defining Characteristic of SHM</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Simple Harmonic Motion (SHM) is a specific type of periodic motion where an object oscillates about a stable equilibrium position. It requires a <strong>Linear Restoring Force</strong>.
            </p>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2">The Mathematical Definition</h4>
                <BlockMath math="F_{net} = -k \Delta x" />
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700 mt-2">
                  <li>The negative sign indicates the force is <strong>opposite</strong> to the displacement.</li>
                  <li><InlineMath math="k" /> is a constant of proportionality (e.g., spring constant).</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900 mb-2">Acceleration in SHM</h4>
                <p className="text-sm text-slate-700 mb-2">Because <InlineMath math="F=ma" />, acceleration is also proportional to displacement:</p>
                <BlockMath math="a = -\frac{k}{m} \Delta x" />
                <p className="text-xs text-red-700 mt-2 font-semibold">Key Concept: In SHM, acceleration is NOT constant. It is maximum at amplitude and zero at equilibrium.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Equilibrium Position</h3>
            <div className="space-y-3">
              <p className="text-slate-700 text-sm">This is the point where the <strong>net force</strong> on the object is zero (<InlineMath math="F_{net} = 0" />).</p>
              <ul className="list-disc pl-6 text-sm text-slate-700">
                <li>The object oscillates back and forth around this point.</li>
                <li>At equilibrium, <strong>velocity is maximum</strong> and <strong>acceleration is zero</strong>.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Restoring Torque (Rotational Systems)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700 mb-2">SHM applies to rotating systems (like a pendulum). For small angles:</p>
              <BlockMath math="\tau = -\kappa \theta" />
              <p className="text-xs text-purple-900 mt-2">This holds true for simple pendulums only at <strong>small angles</strong> (typically <InlineMath math="< 15^\circ" />), where <InlineMath math="\sin \theta \approx \theta" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-2",
      title: "Topic 7.2: Frequency and Period of SHM",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⏱️ Fundamental Definitions</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm 
                term="Period (T)" 
                definition="The time it takes to complete one full cycle (seconds)."
              />
              <KeyTerm 
                term="Frequency (f)" 
                definition="The number of cycles completed per second (Hertz, Hz or s⁻¹)."
              />
            </div>
            <div className="bg-slate-50 p-3 rounded border text-center mt-4">
              <BlockMath math="T = \frac{1}{f}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌿 Mass-Spring Systems (Ideal Spring)</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4 text-center">
              <BlockMath math="T_s = 2\pi \sqrt{\frac{m}{k}}" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-white p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-800 text-sm">Inertia (m)</h5>
                <p className="text-xs text-slate-600 mt-1">Larger mass = more inertia. Moves slower. <strong>Increasing m increases T.</strong></p>
              </div>
              <div className="bg-white p-3 rounded border border-slate-200">
                <h5 className="font-bold text-slate-800 text-sm">Restoring Force (k)</h5>
                <p className="text-xs text-slate-600 mt-1">Stiffer spring = stronger force. Faster acceleration. <strong>Increasing k decreases T.</strong></p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <ImportantNote>
                <strong>Independence of Amplitude:</strong> The period of a mass-spring system does <strong>not</strong> depend on amplitude (A). Double the distance = double the average force, cancelling out the time difference.
              </ImportantNote>
              <ImportantNote>
                <strong>Independence of Gravity:</strong> A vertical spring has the same period as a horizontal one; gravity only shifts the equilibrium position.
              </ImportantNote>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏮 Simple Pendulums</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4 text-center">
              <BlockMath math="T_p = 2\pi \sqrt{\frac{L}{g}}" />
            </div>
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                  <li><strong>Length (L):</strong> Longer string = longer path and higher rotational inertia. <strong>Increasing L increases T.</strong></li>
                  <li><strong>Gravity (g):</strong> Stronger gravity pulls the bob faster. <strong>Increasing g decreases T.</strong></li>
                  <li><strong>Independence of Mass:</strong> Mass cancels out in derivation. <strong>Period is independent of mass.</strong></li>
                  <li><strong>Small Angle:</strong> Valid only for <InlineMath math="< 15^\circ" />.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Experimental Linearization (Lab Skill)</h3>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-bold text-yellow-900">Finding g with a pendulum:</h4>
              <p className="text-sm text-slate-700 mt-1">Instead of graphing T vs L (root curve), graph <strong><InlineMath math="T^2" /> vs <InlineMath math="L" /></strong>.</p>
              <div className="my-3 bg-white p-2 rounded border border-yellow-300">
                <BlockMath math="T^2 = \left(\frac{4\pi^2}{g}\right)L" />
              </div>
              <p className="text-sm text-slate-700">The slope of the best-fit line is <InlineMath math="\frac{4\pi^2}{g}" />. Solve for <InlineMath math="g" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-3",
      title: "Topic 7.3: Representing and Analyzing SHM",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Graphical Representations</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="border border-slate-200 p-2 text-sm">Physical Quantity</th>
                    <th className="border border-slate-200 p-2 text-sm">Graph Shape</th>
                    <th className="border border-slate-200 p-2 text-sm">Relationship to Position</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-slate-700">
                  <tr>
                    <td className="border border-slate-200 p-2 font-bold">Position (x)</td>
                    <td className="border border-slate-200 p-2">Cosine/Sine wave</td>
                    <td className="border border-slate-200 p-2">The reference graph.</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-200 p-2 font-bold">Velocity (v)</td>
                    <td className="border border-slate-200 p-2">Sine/Cosine wave</td>
                    <td className="border border-slate-200 p-2"><strong>Shifted 90° (quarter cycle).</strong> Max at equilibrium (<InlineMath math="x=0" />).</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-200 p-2 font-bold">Acceleration (a)</td>
                    <td className="border border-slate-200 p-2">Cosine/Sine wave</td>
                    <td className="border border-slate-200 p-2"><strong>Shifted 180° (opposite).</strong> Max when <InlineMath math="x" /> is max. Proportional to <InlineMath math="-x" />.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Mathematical Equations</h3>
            <p className="text-sm text-slate-700 mb-4">If an oscillator starts at max positive displacement (<InlineMath math="x=+A" />) at <InlineMath math="t=0" />:</p>
            <div className="bg-slate-800 text-white p-4 rounded-lg mb-4 text-center">
              <BlockMath math="x(t) = A \cos(2\pi f t) \quad \text{or} \quad x(t) = A \cos(\omega t)" />
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-2 text-sm">Kinematic Features</h4>
              <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700">
                <li><strong>Max Displacement (<InlineMath math="x_{max} = A" />):</strong> Occurs at endpoints.</li>
                <li><strong>Max Velocity (<InlineMath math="v_{max}" />):</strong> Occurs at equilibrium.</li>
                <li><strong>Max Acceleration (<InlineMath math="a_{max}" />):</strong> Occurs at endpoints (max spring stretch).</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-4",
      title: "Topic 7.4: Energy of Simple Harmonic Oscillators",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Conservation of Mechanical Energy</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <BlockMath math="E_{total} = K + U = \frac{1}{2}mv^2 + \frac{1}{2}kx^2" />
            </div>
            
            <div className="space-y-4">
              <h4 className="font-bold text-slate-800">The Energy Transformation Cycle:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                  <h5 className="font-bold text-red-900 text-sm">At Amplitude (x = ±A)</h5>
                  <p className="text-xs text-slate-700 mt-1">Velocity is zero. Energy is all <strong>Potential</strong>:</p>
                  <div className="mt-2 text-center"><BlockMath math="E_{total} = U_{s, max} = \frac{1}{2}kA^2" /></div>
                </div>
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                  <h5 className="font-bold text-blue-900 text-sm">At Equilibrium (x = 0)</h5>
                  <p className="text-xs text-slate-700 mt-1">Stretch is zero. Energy is all <strong>Kinetic</strong>:</p>
                  <div className="mt-2 text-center"><BlockMath math="E_{total} = K_{max} = \frac{1}{2}mv_{max}^2" /></div>
                </div>
              </div>
            </div>

            <div className="mt-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900 text-sm">Key Derivation (Max Speed):</h4>
              <p className="text-xs text-slate-700 mt-1">Since <InlineMath math="U_{max} = K_{max}" />:</p>
              <BlockMath math="\frac{1}{2}kA^2 = \frac{1}{2}mv_{max}^2 \Rightarrow v_{max} = A\sqrt{\frac{k}{m}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Energy Graphs</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-white p-3 rounded border border-slate-200">
                <p className="text-xs font-bold text-orange-600">Potential (U vs x)</p>
                <p className="text-[10px] text-slate-500">Parabola opening upward.</p>
              </div>
              <div className="bg-white p-3 rounded border border-slate-200">
                <p className="text-xs font-bold text-blue-600">Kinetic (K vs x)</p>
                <p className="text-[10px] text-slate-500">Inverted parabola (zero at amplitude).</p>
              </div>
              <div className="bg-white p-3 rounded border border-slate-200">
                <p className="text-xs font-bold text-slate-900">Total (E vs x)</p>
                <p className="text-[10px] text-slate-500">Horizontal straight line (constant).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌬️ Damping (Qualitative)</h3>
            <p className="text-sm text-slate-700">Real systems experience <strong>damping</strong> due to friction/drag:</p>
            <ul className="list-disc pl-6 text-sm text-slate-700 mt-2 space-y-1">
              <li>Mechanical energy is dissipated into thermal energy.</li>
              <li>Amplitude decreases exponentially over time.</li>
              <li>The <strong>period remains roughly constant</strong> for light damping.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "equations",
      title: "Unit 7 Equation Summary",
      content: (
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-slate-200">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="border border-slate-200 p-2 text-left text-xs">Concept</th>
                  <th className="border border-slate-200 p-2 text-left text-xs">Equation</th>
                  <th className="border border-slate-200 p-2 text-left text-xs">Notes</th>
                </tr>
              </thead>
              <tbody className="text-xs text-slate-700">
                <tr>
                  <td className="border border-slate-200 p-2 font-bold">Period vs Freq</td>
                  <td className="border border-slate-200 p-2"><InlineMath math="T = \frac{1}{f}" /></td>
                  <td className="border border-slate-200 p-2">-</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="border border-slate-200 p-2 font-bold">Spring Period</td>
                  <td className="border border-slate-200 p-2"><InlineMath math="T_s = 2\pi \sqrt{\frac{m}{k}}" /></td>
                  <td className="border border-slate-200 p-2">Depends on mass and stiffness.</td>
                </tr>
                <tr>
                  <td className="border border-slate-200 p-2 font-bold">Pendulum Period</td>
                  <td className="border border-slate-200 p-2"><InlineMath math="T_p = 2\pi \sqrt{\frac{L}{g}}" /></td>
                  <td className="border border-slate-200 p-2">Depends on length and gravity.</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="border border-slate-200 p-2 font-bold">Hooke's Law</td>
                  <td className="border border-slate-200 p-2"><InlineMath math="|\vec{F}_s| = k|\vec{x}|" /></td>
                  <td className="border border-slate-200 p-2">Restoring force magnitude.</td>
                </tr>
                <tr>
                  <td className="border border-slate-200 p-2 font-bold">Potential Energy</td>
                  <td className="border border-slate-200 p-2"><InlineMath math="U_s = \frac{1}{2}kx^2" /></td>
                  <td className="border border-slate-200 p-2">Elastic potential energy.</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="border border-slate-200 p-2 font-bold">Position Function</td>
                  <td className="border border-slate-200 p-2"><InlineMath math="x = A\cos(2\pi f t)" /></td>
                  <td className="border border-slate-200 p-2">Standard for t=0 at amplitude.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: "misconceptions",
      title: "Common Misconceptions & Exam Tips",
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <p className="text-sm text-slate-800"><strong>1. Period vs. Amplitude:</strong> Students think pulling a pendulum further increases the period. It does <strong>not</strong> (for small angles). Traveling further is offset by a larger force.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <p className="text-sm text-slate-800"><strong>2. Pendulum Mass:</strong> Heavier bobs do <strong>not</strong> swing faster. Mass cancels out because it's in both the force (<InlineMath math="mg\sin\theta" />) and inertia (<InlineMath math="ma" />) terms.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <p className="text-sm text-slate-800"><strong>3. Acceleration:</strong> Acceleration is <strong>NOT constant</strong>. Do not use <InlineMath math="v = v_0 + at" />. Use Energy conservation or SHM graphs.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <p className="text-sm text-slate-800"><strong>4. Freq vs Period:</strong> They are inverse. A stiffer spring (higher k) means lower Period (shorter time) and higher Frequency (faster oscillations).</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
              <p className="text-sm text-slate-800"><strong>5. Vertical Springs:</strong> If you set <InlineMath math="y=0" /> at the <strong>equilibrium position</strong> (where it hangs at rest), you can ignore <InlineMath math="U_g" /> and treat it mathematically like a horizontal spring.</p>
            </div>
          </div>
          <Mnemonic 
            title="Inverse F vs T" 
            content="Fast frequency (high f) = Short period (small T). Slow frequency (low f) = Long period (large T)." 
          />
        </div>
      )
    }
  ]
};

export default physicsUnit7Notes;